import { Radio } from "lucide-react"
import { useTranslation } from "react-i18next"
import {
  TASK_ORDERS,
  TASK_PRIORITIES,
  TASK_STATUS_FILTERS,
  type TaskFilters,
  type TaskOrder,
} from "@/api/queries/tasks"
import type { SubscriptionStatus } from "@/api/sse"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { parseAssigneeKey } from "./logic"
import { PriorityLabel, STATUS_DOT, useTaskLabels } from "./task-badges"
import { useAssigneeOptions, useProjectOptions } from "./task-form"

/** Filter values as kept in the URL (`?status=&project=&assignee=&priority=&sort=`). */
export interface TaskFilterSearch {
  q?: string
  status?: string
  /**
   * Project id, or `none` for standalone tasks. A number, so the URL reads `?project=12`
   * (the router JSON-quotes numeric-looking strings: `?project=%2212%22`).
   */
  project?: number | "none"
  /** `agent:3`, `team:2`, `pool` */
  assignee?: string
  priority?: string
  sort?: TaskOrder
}

const str = (v: unknown) =>
  typeof v === "string" && v ? v : v != null && typeof v === "number" ? String(v) : undefined

const projectParam = (v: unknown): TaskFilterSearch["project"] => {
  if (v === "none") return "none"
  const n = Number(v)
  return (typeof v === "number" || (typeof v === "string" && v)) && Number.isInteger(n) && n > 0
    ? n
    : undefined
}

/** Parses filter search params (shared by the list and the board). */
export function parseTaskFilterSearch(s: Record<string, unknown>): TaskFilterSearch {
  const sort = str(s.sort)
  return {
    q: str(s.q),
    status: str(s.status),
    project: projectParam(s.project),
    assignee: str(s.assignee),
    priority: str(s.priority),
    sort: (TASK_ORDERS as readonly string[]).includes(sort ?? "") ? (sort as TaskOrder) : undefined,
  }
}

/** URL filters → `GET /tasks` query. */
export function toTaskFilters(s: TaskFilterSearch): TaskFilters {
  const a = parseAssigneeKey(s.assignee)
  return {
    q: s.q,
    status: s.status,
    project_id: typeof s.project === "number" ? s.project : undefined,
    standalone: s.project === "none" ? true : undefined,
    assignee_kind: a?.assignee_kind,
    assignee_id: a && "assignee_id" in a ? a.assignee_id : undefined,
    priority: s.priority,
    order_by: s.sort,
  }
}

export const hasTaskFilters = (s: TaskFilterSearch, ignore: (keyof TaskFilterSearch)[] = []) =>
  (["q", "status", "project", "assignee", "priority"] as const).some((k) => !ignore.includes(k) && !!s[k])

const ALL = "all"

/** Filter selects for the task list/board toolbar. */
export function TaskFilterControls({
  value,
  onChange,
  showStatus = true,
  showSort = true,
}: {
  value: TaskFilterSearch
  onChange: (patch: Partial<TaskFilterSearch>) => void
  showStatus?: boolean
  showSort?: boolean
}) {
  const { t } = useTranslation()
  const labels = useTaskLabels()
  const projects = useProjectOptions()
  const assignees = useAssigneeOptions()
  const pick = (v: string | null) => (v == null || v === ALL ? undefined : v)
  return (
    <>
      {showStatus && (
        <Select value={value.status ?? ALL} onValueChange={(v) => onChange({ status: pick(v) })}>
          <SelectTrigger size="sm" className="h-8 w-40" aria-label={t("tasks.task.fields.status")}>
            <SelectValue>
              {(v: string) => (v === ALL ? t("tasks.hub.filters.allStatuses") : labels.status(v))}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t("tasks.hub.filters.allStatuses")}</SelectItem>
            {TASK_STATUS_FILTERS.map((s) => (
              <SelectItem key={s} value={s}>
                <span
                  className={cn("size-2 rounded-full", STATUS_DOT[s] ?? "bg-muted-foreground/40")}
                  aria-hidden
                />
                {labels.status(s)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Select
        value={value.project != null ? String(value.project) : ALL}
        onValueChange={(v) => onChange({ project: projectParam(pick(v)) })}
      >
        <SelectTrigger size="sm" className="h-8 w-40" aria-label={t("tasks.task.fields.project")}>
          <SelectValue>
            {(v: string) =>
              v === ALL
                ? t("tasks.hub.filters.allProjects")
                : v === "none"
                  ? t("tasks.hub.filters.standalone")
                  : (projects.find((p) => String(p.value) === v)?.label ?? `#${v}`)
            }
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t("tasks.hub.filters.allProjects")}</SelectItem>
          <SelectItem value="none">{t("tasks.hub.filters.standalone")}</SelectItem>
          {projects.map((p) => (
            <SelectItem key={p.value} value={String(p.value)}>
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={value.assignee ?? ALL} onValueChange={(v) => onChange({ assignee: pick(v) })}>
        <SelectTrigger size="sm" className="h-8 w-40" aria-label={t("tasks.task.fields.assignee")}>
          <SelectValue>
            {(v: string) =>
              v === ALL
                ? t("tasks.hub.filters.allAssignees")
                : (assignees.find((a) => a.value === v)?.label ?? v)
            }
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t("tasks.hub.filters.allAssignees")}</SelectItem>
          {assignees.map((a) => (
            <SelectItem key={a.value} value={a.value}>
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{a.label}</span>
                {a.description && <span className="text-muted-foreground text-xs">{a.description}</span>}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={value.priority ?? ALL} onValueChange={(v) => onChange({ priority: pick(v) })}>
        <SelectTrigger size="sm" className="h-8 w-36" aria-label={t("tasks.task.fields.priority")}>
          <SelectValue>
            {(v: string) =>
              v === ALL ? t("tasks.hub.filters.allPriorities") : <PriorityLabel priority={v} />
            }
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{t("tasks.hub.filters.allPriorities")}</SelectItem>
          {TASK_PRIORITIES.map((p) => (
            <SelectItem key={p} value={p}>
              <PriorityLabel priority={p} />
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {showSort && (
        <Select
          value={value.sort ?? "updated_at"}
          onValueChange={(v) =>
            onChange({ sort: v === "updated_at" || v == null ? undefined : (v as TaskOrder) })
          }
        >
          <SelectTrigger size="sm" className="h-8 w-40" aria-label={t("tasks.hub.filters.sort")}>
            <SelectValue>
              {(v: string) => t("tasks.hub.filters.sortBy", { order: labels.order(v) })}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {TASK_ORDERS.map((o) => (
              <SelectItem key={o} value={o}>
                {labels.order(o)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </>
  )
}

/** "Live" dot for the realtime board stream. */
export function LiveIndicator({ status }: { status: SubscriptionStatus }) {
  const { t } = useTranslation()
  const live = status === "open"
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            className={cn(
              "inline-flex items-center gap-1.5 text-xs",
              live ? "text-success" : "text-muted-foreground",
            )}
          />
        }
      >
        <Radio className={cn("size-3.5", live && "animate-pulse")} aria-hidden />
        {live ? t("tasks.hub.live") : t("tasks.hub.offline")}
      </TooltipTrigger>
      <TooltipContent>{live ? t("tasks.hub.liveHint") : t("tasks.hub.offlineHint")}</TooltipContent>
    </Tooltip>
  )
}
